import { ReactNode, RefObject, useCallback, useEffect, useRef } from "react";

import {
  autoPlacement,
  autoUpdate,
  computePosition,
  offset,
  size,
} from "@floating-ui/react";

import { useOnClickOutside } from "../../hooks/useOnClickOutside/useOnClickOutside";

import styles from "./OptionsPopover.module.scss";

export type OptionsPopoverProps = {
  id: string;
  children: ReactNode;
  referenceElement: RefObject<HTMLElement>;
  open: boolean;
  onClickOutside: () => void;
  /** Controls the popover width strategy.
   * - `"reference"` — match the reference element's width
   * - `number` — fixed width in px
   * - `string` — any CSS width value (e.g. `"20rem"`)
   * - `undefined` (default) — intrinsic sizing (`max-content`)
   */
  width?: "reference" | number | string;
};

function resolveWidth(
  width: OptionsPopoverProps["width"],
  referenceWidth: number,
): string | undefined {
  if (width === "reference") return `${referenceWidth}px`;
  if (typeof width === "number") return `${width}px`;
  if (typeof width === "string") return width;
  return undefined;
}

export const OptionsPopover = (props: OptionsPopoverProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { id, children, referenceElement, open, onClickOutside, width } = props;

  useOnClickOutside({
    targets: [ref.current ?? undefined, referenceElement.current ?? undefined],
    onClickOutside: onClickOutside,
    disable: !open,
  });

  useEffect(() => {
    if (open) {
      ref.current?.showPopover();
    } else {
      ref.current?.hidePopover();
    }
  }, [open]);

  const updatePosition = useCallback(async () => {
    if (!ref.current || !referenceElement.current) return;
    const position = await computePosition(
      referenceElement.current,
      ref.current,
      {
        placement: "bottom-start",
        middleware: [
          offset(8),
          autoPlacement({
            allowedPlacements: [
              "bottom-start",
              "bottom-end",
              "top-start",
              "top-end",
            ],
            padding: 8,
          }),
          size({
            padding: 8,
            apply({ rects, elements, availableHeight, availableWidth }) {
              const resolved = resolveWidth(width, rects.reference.width);
              Object.assign(elements.floating.style, {
                ...(resolved != null && { width: resolved }),
                maxWidth: `${Math.max(0, availableWidth)}px`,
                maxHeight: `${Math.max(0, availableHeight)}px`,
              });
            },
          }),
        ],
      },
    );
    ref.current.style.left = `${position.x}px`;
    ref.current.style.top = `${position.y}px`;
  }, [ref, referenceElement, width]);

  useEffect(() => {
    if (!ref.current || !referenceElement.current || !open) return;
    const cleanup = autoUpdate(
      referenceElement.current,
      ref.current,
      updatePosition,
    );
    return () => {
      cleanup();
    };
  }, [referenceElement, ref, updatePosition, open]);

  return (
    <div
      ref={ref}
      id={id}
      className={styles["options-popover"]}
      popover="manual"
    >
      {children}
    </div>
  );
};
